import { useCallback, useMemo } from 'react';
import { Text, TextElementTypes } from '@picsart/design-system/components/Text';
import {
  InlineButton,
  ControlSizesMD,
  ControlSizesLG,
  InlineButtonAlignments,
} from '@picsart/design-system/components/InlineButton';
import { Divider } from '@picsart/design-system/components/Divider';
import {
  Button,
  ButtonSizeMD,
  ButtonSkinChristmasRed,
  ButtonSkinPrimary,
  ButtonVariants,
} from '@picsart/design-system/components/Button';
import { IconChevronLeft } from '@picsart/design-system/foundation/icons/IconChevronLeft';
import { IconDelete } from '@picsart/design-system/foundation/icons/IconDelete';
import { IconLogout } from '@picsart/design-system/foundation/icons/IconLogout';
import { TypographyT4 } from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { getContext } from '@/context';
import { logout, deleteAccount } from '@/utils/user/authActions';
import useStyles from './styles';

const formatVersionCode = (raw: string | undefined): string => {
  if (!raw) return '';
  const trimmed = raw.slice(4);
  const major = trimmed.slice(0, 2);
  const minor = trimmed.slice(2, 3);
  const patch = trimmed.slice(3);
  if (!patch || /^0+$/.test(patch)) return `${major}.${minor}`;
  const patchFormatted = patch.length === 1 ? `0${patch}` : patch;
  return `${major}.${minor}.${patchFormatted}`;
};

const SUPPORT_EMAIL = 'support@picsart.com';

const HELP_LINKS = [
  {
    label: 'Contact Support',
    url: `mailto:${SUPPORT_EMAIL}?subject=GenAI%20Studio%20Support%20Request`,
  },
  { label: 'Help Center', url: 'https://support.picsart.com/hc/en-us' },
];

const ABOUT_LINKS = [
  { label: 'Terms of Use', url: 'https://picsart.com/terms-of-use' },
  {
    label: 'License Contribution',
    url: 'https://picsart.com/license/ios/?app=1',
  },
  { label: 'Privacy Policy', url: 'https://picsart.com/privacy-policy' },
  {
    label: 'Do Not Sell or Share My Personal Information',
    url: 'https://privacy.picsart.com/optout?app=1',
  },
  { label: 'DMCA', url: 'https://picsart.com/copyright-dispute-policy' },
];

interface SettingsPanelProps {
  variant: 'dropdown' | 'page';
  onBack?: () => void;
  isAuthorized?: boolean;
}

export const SettingsPanel = ({
  variant,
  onBack,
  isAuthorized = false,
}: SettingsPanelProps) => {
  const classes = useStyles();
  const isPage = variant === 'page';

  const openUrl = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleLogout = useCallback(() => logout(), []);

  const handleDeleteAccount = useCallback(() => deleteAccount(), []);

  const appVersion = useMemo(() => {
    try {
      const versionCode = getContext().utils.api.getHeaders()?.versioncode;
      return formatVersionCode(versionCode);
    } catch {
      return '';
    }
  }, []);

  const renderLinkItems = (links: typeof HELP_LINKS) =>
    links.map((link) => (
      <InlineButton
        key={link.label}
        data-testid={`genai-settings-link-${link.label
          .toLowerCase()
          .replace(/\s+/g, '-')}`}
        size={ControlSizesLG}
        isFullWidth
        isWithoutBorder
        isWithoutBackground
        alignment={InlineButtonAlignments.Start}
        onClick={() => openUrl(link.url)}
        className={classes.menuItem}
      >
        <Text elementType={TextElementTypes.Span}>{link.label}</Text>
      </InlineButton>
    ));

  return (
    <div className={isPage ? classes.page : classes.dropdown}>
      {isPage && onBack && (
        <div className={classes.pageHeader}>
          <InlineButton
            data-testid="genai-settings-back"
            size={ControlSizesMD}
            centerIcon={IconChevronLeft}
            onClick={onBack}
            aria-label="Back"
            isWithoutBorder
            isWithoutBackground
          />
          <Text
            elementType={TextElementTypes.Span}
            className={classes.pageTitle}
          >
            Settings
          </Text>
        </div>
      )}

      {!isPage && (
        <div className={classes.dropdownHeader}>
          <Text
            elementType={TextElementTypes.Span}
            className={classes.pageTitle}
          >
            Settings
          </Text>
        </div>
      )}

      <div className={classes.content}>
        <div className={classes.section}>
          <Text
            size={TypographyT4}
            weight={FontWeights.semibold}
            className={classes.sectionTitle}
          >
            Help
          </Text>
          {renderLinkItems(HELP_LINKS)}
        </div>

        <Divider />

        <div className={classes.section}>
          <Text
            size={TypographyT4}
            weight={FontWeights.semibold}
            className={classes.sectionTitle}
          >
            About
          </Text>
          {renderLinkItems(ABOUT_LINKS)}
          <div className={classes.versionItem}>
            <Text
              elementType={TextElementTypes.Span}
              className={classes.versionLabel}
            >
              Version
            </Text>
            <Text
              elementType={TextElementTypes.Span}
              className={classes.versionValue}
            >
              {appVersion}
            </Text>
          </div>
        </div>
      </div>

      {isAuthorized && (
        <div className={classes.actions}>
          <Button
            dataTestId="genai-settings-delete-account"
            size={ButtonSizeMD}
            skin={ButtonSkinChristmasRed}
            variant={ButtonVariants.Outline}
            startIcon={IconDelete}
            onClick={handleDeleteAccount}
            isFullWidth
          >
            Delete account
          </Button>
          <Button
            dataTestId="genai-settings-log-out"
            size={ButtonSizeMD}
            skin={ButtonSkinPrimary}
            variant={ButtonVariants.Outline}
            startIcon={IconLogout}
            onClick={handleLogout}
            isFullWidth
          >
            Log Out
          </Button>
        </div>
      )}
    </div>
  );
};
